import { useMemo, useState } from 'react';
import { makeStyles, tokens, Text } from '@fluentui/react-components';
import type { CostQualityScatterProps, ExecSeriesPoint } from './execTypes';

// Hand-rolled SVG scatter plot.
// * x-axis: cost in log scale (fallback domain when only 1 point).
// * y-axis: overall score 0..1 (linear).
// * viewBox pixel-space (600 × H) + vector-effect: non-scaling-stroke.
// * HTML tooltip layer over SVG; <title> for screen-reader fallback.
// * Error points rendered as red "×" glyph at bottom-right with legend line.
// * TODO: overlap resolution for dots with very close pixel positions is
//   deferred — both labels render and may overlap. Implement a force-nudge
//   pass if label collisions become a UX issue with >8 providers.

const PROVIDER_COLORS: Record<string, string> = {
  openai: '#10a37f',
  anthropic: '#c47a3b',
  'azure-openai': '#0078d4',
  'azure-ai-foundry': '#7719aa',
};

const FALLBACK_COLORS = ['#0078d4', '#10a37f', '#c47a3b', '#7719aa', '#e3008c', '#5c2d91'];

function colorFor(point: ExecSeriesPoint, index: number): string {
  if (point.color) return point.color;
  return PROVIDER_COLORS[point.key] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

const VIEW_W = 600;
const PAD_L = 44;
const PAD_R = 80;  // extra room for quality-floor label on the right
const PAD_T = 16;
const PAD_B = 32;

const DOT_R = 4;
const LABEL_OFFSET = 6;

const useStyles = makeStyles({
  root: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '14px 16px',
    marginBottom: '16px',
    position: 'relative',
  },
  header: {
    marginBottom: '10px',
  },
  chartWrap: {
    position: 'relative',
    width: '100%',
  },
  tooltip: {
    position: 'absolute',
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '6px',
    boxShadow: tokens.shadow8,
    padding: '8px 10px',
    fontSize: '12px',
    lineHeight: 1.35,
    pointerEvents: 'none',
    transform: 'translate(-50%, -100%)',
    marginTop: '-8px',
    whiteSpace: 'nowrap' as const,
    zIndex: 2,
  },
  errorLegend: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginTop: '6px',
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    padding: '24px 0',
    textAlign: 'center' as const,
    fontStyle: 'italic',
  },
});

interface HoverState {
  point: ExecSeriesPoint;
  color: string;
  xPct: number;
  yPct: number;
}

function formatCost(v: number): string {
  if (v === 0) return '$0';
  if (v < 0.001) return `$${v.toFixed(6)}`;
  if (v < 0.01) return `$${v.toFixed(5)}`;
  if (v < 0.1) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(3)}`;
}

export function CostQualityScatter({
  points,
  title,
  height = 260,
  qualityFloor = 0.6,
}: CostQualityScatterProps) {
  const s = useStyles();
  const [hover, setHover] = useState<HoverState | null>(null);

  const { activePoints, errorPoints, originalIndices } = useMemo(() => {
    const active: ExecSeriesPoint[] = [];
    const errors: ExecSeriesPoint[] = [];
    const indices: Map<string, number> = new Map();
    points.forEach((p, i) => {
      if (p.isError) {
        errors.push(p);
      } else {
        indices.set(p.key, i);
        active.push(p);
      }
    });
    return { activePoints: active, errorPoints: errors, originalIndices: indices };
  }, [points]);

  const chart = useMemo(() => {
    if (activePoints.length === 0) return null;

    const innerW = VIEW_W - PAD_L - PAD_R;
    const innerH = height - PAD_T - PAD_B;

    // x-axis: log-scale cost
    const costs = activePoints.map((p) => Math.max(p.costUsd, 1e-9));
    const minCost = Math.min(...costs);
    const maxCost = Math.max(...costs);

    // Fallback domain when only 1 point (spec requirement)
    const logMin = activePoints.length === 1
      ? Math.log10(minCost * 0.5)
      : Math.log10(minCost);
    const logMax = activePoints.length === 1
      ? Math.log10(minCost * 2)
      : Math.log10(maxCost);
    const logSpan = logMax - logMin || 1;

    const xFor = (cost: number) => {
      const safeLog = Math.log10(Math.max(cost, 1e-9));
      return PAD_L + ((safeLog - logMin) / logSpan) * innerW;
    };

    // y-axis: linear 0..1
    const yFor = (score: number) => PAD_T + (1 - score) * innerH;

    // x ticks: min, geometric mid, max
    const geoMid = Math.pow(10, (logMin + logMax) / 2);
    const xTicks = [
      { v: Math.pow(10, logMin), label: formatCost(Math.pow(10, logMin)) },
      { v: geoMid, label: formatCost(geoMid) },
      { v: Math.pow(10, logMax), label: formatCost(Math.pow(10, logMax)) },
    ];

    // y ticks at 0, 0.25, 0.5, 0.75, 1.0
    const yTicks = [0, 0.25, 0.5, 0.75, 1.0];

    // Quality floor y position
    const floorY = yFor(qualityFloor);

    return { xFor, yFor, xTicks, yTicks, floorY, innerH, innerW };
  }, [activePoints, height, qualityFloor]);

  const allEmpty = activePoints.length === 0 && errorPoints.length === 0;

  if (allEmpty) {
    return (
      <div className={s.root}>
        {title && (
          <div className={s.header}>
            <Text size={500} weight="semibold">{title}</Text>
          </div>
        )}
        <div className={s.empty}>No comparable data</div>
      </div>
    );
  }

  // Error glyph position — bottom-right of chart area
  const errorGlyphX = VIEW_W - PAD_R + 8;
  const errorGlyphY = height - PAD_B - 2;

  return (
    <div className={s.root}>
      {title && (
        <div className={s.header}>
          <Text size={500} weight="semibold">{title}</Text>
        </div>
      )}
      <div className={s.chartWrap}>
        <svg
          viewBox={`0 0 ${VIEW_W} ${height}`}
          role="img"
          aria-label={title ?? 'Cost vs quality scatter'}
          style={{ width: '100%', height, display: 'block' }}
        >
          {chart && (
            <>
              {/* y-axis gridlines + labels */}
              {chart.yTicks.map((v) => (
                <g key={v}>
                  <line
                    x1={PAD_L}
                    x2={VIEW_W - PAD_R}
                    y1={chart.yFor(v)}
                    y2={chart.yFor(v)}
                    stroke={tokens.colorNeutralStroke2}
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                    strokeDasharray={v === 0 ? undefined : '2 3'}
                  />
                  <text
                    x={PAD_L - 4}
                    y={chart.yFor(v)}
                    fontSize={11}
                    textAnchor="end"
                    dominantBaseline="central"
                    fill={tokens.colorNeutralForeground3}
                  >
                    {(v * 100).toFixed(0)}%
                  </text>
                </g>
              ))}

              {/* x-axis labels */}
              {chart.xTicks.map(({ v, label }, i) => (
                <text
                  key={i}
                  x={chart.xFor(v)}
                  y={height - PAD_B + 16}
                  fontSize={11}
                  textAnchor={i === 0 ? 'start' : i === chart.xTicks.length - 1 ? 'end' : 'middle'}
                  fill={tokens.colorNeutralForeground3}
                >
                  {label}
                </text>
              ))}

              {/* Quality floor dashed line */}
              <line
                x1={PAD_L}
                x2={VIEW_W - PAD_R}
                y1={chart.floorY}
                y2={chart.floorY}
                stroke={tokens.colorNeutralForeground3}
                strokeWidth={1}
                strokeDasharray="4 3"
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={VIEW_W - PAD_R + 4}
                y={chart.floorY}
                fontSize={10}
                dominantBaseline="central"
                fill={tokens.colorNeutralForeground3}
              >
                {`Quality floor ${(qualityFloor * 100).toFixed(0)}%`}
              </text>

              {/* Data points */}
              {activePoints.map((point) => {
                const originalIdx = originalIndices.get(point.key) ?? 0;
                const color = colorFor(point, originalIdx);
                const cx = chart.xFor(Math.max(point.costUsd, 1e-9));
                const cy = chart.yFor(point.overallScore);
                const xPct = (cx / VIEW_W) * 100;
                const yPct = (cy / height) * 100;

                return (
                  <g key={point.key}>
                    <circle cx={cx} cy={cy} r={DOT_R} fill={color}>
                      <title>{`${point.label}: ${(point.overallScore * 100).toFixed(0)}% overall, ${formatCost(point.costUsd)}/query`}</title>
                    </circle>

                    {/* Label */}
                    <text
                      x={cx + DOT_R + LABEL_OFFSET}
                      y={cy}
                      fontSize={11}
                      dominantBaseline="central"
                      fill={tokens.colorNeutralForeground2}
                    >
                      {point.label}
                    </text>

                    {/* Transparent hit target */}
                    <circle
                      cx={cx}
                      cy={cy}
                      r={12}
                      fill="transparent"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHover({ point, color, xPct, yPct })}
                      onMouseLeave={() => setHover(null)}
                    />
                  </g>
                );
              })}
            </>
          )}

          {/* Error points as × glyph — stacked at bottom-right */}
          {errorPoints.length > 0 && (
            <g>
              <text
                x={errorGlyphX}
                y={errorGlyphY}
                fontSize={14}
                fontWeight={700}
                fill={tokens.colorPaletteRedForeground1}
                textAnchor="middle"
                dominantBaseline="central"
              >
                ×
              </text>
            </g>
          )}
        </svg>

        {hover && (
          <div
            className={s.tooltip}
            style={{ left: `${hover.xPct}%`, top: `${hover.yPct}%` }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: hover.color, flexShrink: 0 }} />
              {hover.point.label}
            </div>
            <div>Overall: <strong>{(hover.point.overallScore * 100).toFixed(0)}%</strong></div>
            <div>Cost: <strong>{formatCost(hover.point.costUsd)}/query</strong></div>
            <div>Latency: <strong>{hover.point.latencyMs.toFixed(0)} ms</strong></div>
            <div>Hallucination: <strong>{(hover.point.hallucinationRisk * 100).toFixed(0)}%</strong></div>
          </div>
        )}
      </div>

      {errorPoints.length > 0 && (
        <div className={s.errorLegend}>
          <span style={{ color: tokens.colorPaletteRedForeground1, fontSize: 14, fontWeight: 700 }}>×</span>
          {`${errorPoints.length} errored`}
        </div>
      )}
    </div>
  );
}
